import React, { useEffect, useState } from "react";
import { getJobs } from "@/api/jobs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowUpDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { formatTimeRange } from "@/lib/formatTimeRange.js";
import {formatDateRange} from "@/lib/formatDateRange.js";
import {useSearchParams} from "react-router-dom";
import {getTimesheetsPdf} from "@/api/timesheets.jsx";

export default function JobsIndex() {
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState("startDate");
    const [sortAsc, setSortAsc] = useState(true);
    const [searchParams] = useSearchParams();
    const [isDownloading, setIsDownloading] = useState(false);
    const navigate = useNavigate();
    const activeScope = searchParams.get("scope") || "all";

    const handleDownloadPDF = async (jobId) => {

        setIsDownloading(true)
        try {
            const response = await getTimesheetsPdf(jobId)
            if (!response.ok) throw new Error("Failed to download PDF");
            console.log(response)

            const blob = await response.blob();

            // 🔍 Extract filename from Content-Disposition
            const disposition = response.headers.get("Content-Disposition");
            let filename = "timesheet.pdf"; // default fallback

            if (disposition && disposition.includes("filename=")) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    filename = match[1];
                }
            }

            // Trigger download
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error("Error downloading PDF:", error);
            // Show toast or error state
        }
        setIsDownloading(false)
    };



    useEffect(() => {
        async function fetchJobs() {
            const queryParams = Object.fromEntries([...searchParams.entries()]);
            const data = await getJobs(queryParams);
            if (Array.isArray(data)) {
                setJobs(data);
                setFilteredJobs(data);
            }
        }
        fetchJobs();
    }, [searchParams]);

    useEffect(() => {
        let filtered = [...jobs];
        if (search) {
            filtered = filtered.filter(
                (job) =>
                    job.venue?.name?.toLowerCase().includes(search.toLowerCase()) ||
                    job.candidate?.name?.toLowerCase().includes(search.toLowerCase())
            );
        }
        filtered.sort((a, b) => {
            const valA = a[sortKey];
            const valB = b[sortKey];
            if (valA < valB) return sortAsc ? -1 : 1;
            if (valA > valB) return sortAsc ? 1 : -1;
            return 0;
        });
        setFilteredJobs(filtered);
    }, [search, sortKey, sortAsc, jobs]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            setSortAsc(true);
        }
    };



    const formatTimeDisplay = (startTime, endTime) => {
        const { start, end, duration } = formatTimeRange(startTime, endTime);
        return (
            <span>
        {start} <span className="text-gray-400">to</span> {end}{" "}
                <span className="text-blue-600">({duration} hour{duration !== 1 ? "s" : ""})</span>
      </span>
        );
    };

    const formatDateDisplay = (startTime, endTime) => {
        const { start, end, duration } = formatDateRange(startTime, endTime);
        return (
            <span>
        {start} <span className="text-gray-400">to</span> {end}{" "}
                <span className="text-blue-600">({duration} hour{duration !== 1 ? "s" : ""})</span>
      </span>
        );
    };

    const handleFilterChange = (scope) => {
        if (scope === "all") {
            navigate("/admin/jobs");
        } else {
            navigate(`/admin/jobs?scope=${scope}`);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center gap-4">
                <Input
                    placeholder="Search jobs..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-sm"
                />
                <Link to="/admin/jobs/new">
                    <Button className='bg-brand-500'>Add Job</Button>
                </Link>
            </div>

            <div className="flex gap-2 flex-wrap">
                <Button
                    variant={activeScope === "all" ? "default" : "outline"}
                    onClick={() => handleFilterChange("all")}
                    className={activeScope === "all" ? "bg-brand-500" : ""}
                >
                    All
                </Button>
                <Button
                    variant={activeScope === "previous" ? "default" : "outline"}
                    onClick={() => handleFilterChange("previous")}
                    className={activeScope === "previous" ? "bg-brand-500" : ""}
                >
                    Previous
                </Button>
                <Button
                    variant={activeScope === "upcoming" ? "default" : "outline"}
                    onClick={() => handleFilterChange("upcoming")}
                    className={activeScope === "upcoming" ? "bg-brand-500" : ""}
                >
                    Upcoming
                </Button>
                <Button
                    variant={activeScope === "today" ? "default" : "outline"}
                    onClick={() => handleFilterChange("today")}
                    className={activeScope === "today" ? "bg-brand-500" : ""}
                >
                    Today
                </Button>
                <Button
                    variant={activeScope === "this_week" ? "default" : "outline"}
                    onClick={() => handleFilterChange("this_week")}
                    className={activeScope === "this_week" ? "bg-brand-500" : ""}
                >
                    This Week
                </Button>
                <Button
                    variant={activeScope === "this_month" ? "default" : "outline"}
                    onClick={() => handleFilterChange("this_month")}
                    className={activeScope === "this_month" ? "bg-brand-500" : ""}
                >
                    This Month
                </Button>
            </div>

            <div className="bg-white shadow rounded-xl overflow-auto">
                <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                    <tr>
                        <th className="px-4 py-3 cursor-pointer" onClick={() => toggleSort("startDate")}>
                            Dates <ArrowUpDown className="inline w-3 h-3" />
                        </th>
                        <th className="px-4 py-3 cursor-pointer" onClick={() => toggleSort("startTime")}>
                            Hours <ArrowUpDown className="inline w-3 h-3" />
                        </th>
                        <th className="px-4 py-3 cursor-pointer" onClick={() => toggleSort("venue.name")}>
                            Venue
                        </th>
                        <th className="px-4 py-3 cursor-pointer" onClick={() => toggleSort("skill.name")}>
                            Skill
                        </th>
                        <th className="px-4 py-3 cursor-pointer" onClick={() => toggleSort("candidate.name")}>
                            Candidate
                        </th>
                        <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredJobs.map((job) => (
                        <tr
                            key={job.id}
                            className={`border-b ${!job.candidate ? "bg-yellow-100" : ""}`}
                        >
                            <td className="px-4 py-2">{formatDateDisplay(job.startDate, job.endDate)}</td>
                            <td className="px-4 py-2">{formatTimeDisplay(job.startTime, job.endTime)}</td>
                            <td className="px-4 py-2">{job.venue?.name || "—"}</td>
                            <td className="px-4 py-2">{job.skill?.name || "—"}</td>
                            <td className="px-4 py-2">
                                {job.candidate ? (
                                    <Link
                                        to={`/admin/candidate/${job.candidate.slugId}`}
                                        className="text-[#5D1F42] hover:underline"
                                    >
                                        {job.candidate.name}
                                    </Link>
                                ) : (
                                    <span className="text-yellow-800">Unclaimed</span>
                                )}
                            </td>
                            <td className="px-4 py-2 text-right space-x-2">
                                <Link
                                    to={`/admin/jobs/${job.id}`}
                                    className="text-sm text-[#5D1F42] hover:underline"
                                >
                                    View
                                </Link>

                                <Button
                                    variant="link"
                                    size="sm"
                                    disabled={isDownloading}
                                    onClick={() => handleDownloadPDF(job.id)}
                                >
                                    {isDownloading ? "Downloading..." : "Download Timesheet"}
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
